import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Row,
    Col,
    Input,
    Button,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "reactstrap";
import {
    fetchActivePosByCompany,
    fetchActiveBankBrands,
    migrateBrandToVirtualPos, refreshBrandInstallments, setActivePosStandart,
    getActivePosOrphanSummary, migrateAllBrandsToVirtualPos
} from "../../../api/requests/company-requests"
import { CARD_BRAND_BANK_LABELS } from "../../../utils/paymentLinkUtils";
import { toast } from "react-toastify";

const NAVY = "#0A316E";

const CARD_BRAND_LABELS = {
    WORLD: "World",
    AXESS: "Axess",
    MAXIMUM: "Maximum",
    CARDFINANS: "CardFinans",
    PARAF: "Paraf",
    ADVANTAGE: "Advantage",
    COMBO: "Combo",
    BONUS: "Bonus",
    SAGLAMKART: "Sağlam Kart",
    STANDART: "Diğer (Banka Kartı)"
};

const VPOS_LABELS = {
    PAYTR: "PayTR",
    PARAMPOS: "Param POS",
    IYZICO: "iyzico",
    ESNEK_POS: "Hoppa",
    QNB_PAYFOR: "QNB PayFor",
    GARANTI: "Garanti BBVA",
    ISYERIM_POS: "İşyerimPOS"
};

// Kart markası → logo (public/images altındaki mevcut görseller).
// Kendi görseli olmayan marka (STANDART) ikon fallback'ine düşer.
const CARD_BRAND_LOGOS = {
    WORLD: "/images/payments/yapi-kredi.png",
    AXESS: "/images/payments/akbank-axess.svg",
    MAXIMUM: "/images/payments/isbank-maximum.svg",
    CARDFINANS: "/images/banks/finansbank.png",
    PARAF: "/images/payments/paraf.svg",
    ADVANTAGE: "/images/payments/hsbc.svg",
    COMBO: "/images/payments/bankkart-combo.svg",
    BONUS: "/images/payments/garanti.svg",
    SAGLAMKART: "/images/payments/saglamkart.svg",
};

// refreshKey: üst bileşen bir sanal POS bağlandığında/kaldırıldığında artırır;
// aktif POS ve kart markası listeleri yeniden çekilir (eskiden F5 gerekiyordu).
export default function ActivePosSection({ companyId, refreshKey = 0 }) {
    const [posSearch, setPosSearch] = useState("");
    const [posDropdownOpen, setPosDropdownOpen] = useState(false);

    const [refreshingBrandId, setRefreshingBrandId] = useState(null);
    const [standartSaving, setStandartSaving] = useState(false);
    const [openCardBrand, setOpenCardBrand] = useState(null);

    const [banks, setBanks] = useState([]); // API: data.banks
    const [loading, setLoading] = useState(false);

    const [activePosBrands, setActivePosBrands] = useState({});

    const [pendingMigration, setPendingMigration] = useState(null);
    const [isMigrating, setIsMigrating] = useState(false);

    // Toplu aktarım: hedefler firmanın gerçekten kurulu POS'ları. Satır, öksüz marka
    // kalmasa da durur — kullanıcı markaları istediği zaman tek seferde taşıyabilsin.
    const [transferTargets, setTransferTargets] = useState([]);
    const [orphanCount, setOrphanCount] = useState(0);
    const [orphanTarget, setOrphanTarget] = useState("");
    const [movingOrphans, setMovingOrphans] = useState(false);
    const [refreshingAll, setRefreshingAll] = useState(false);


    const toggleCardBrand = (id) => {
        setOpenCardBrand((prev) => (prev === id ? null : id));
    };

    // Aktif POS + bankaları yükleyen fonksiyon (tekrar kullanmak için useCallback)
    const loadActivePos = useCallback(
        async (cid) => {
            if (!cid) return;
            try {
                setLoading(true);
                const res = await fetchActivePosByCompany(cid);
                const data = res?.data;

                if (!data || !Array.isArray(data.banks)) {
                    setBanks([]);
                    return;
                }

                setBanks(data.banks);
            } catch (e) {
                console.error("ActivePos load error", e);
                setBanks([]);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    // Aktif kart markalarını yükleyen fonksiyon
    const loadActiveBrands = useCallback(
        async (cid) => {
            if (!cid) return;
            try {
                const generic = await fetchActiveBankBrands(cid);
                const data = generic?.data;

                if (!data?.activeBrandsByPos) {
                    setActivePosBrands({});
                    return;
                }

                const byPos = {};
                Object.entries(data.activeBrandsByPos).forEach(([vpos, brandsMap]) => {
                    const activeBrands = Object.entries(brandsMap || {})
                        .filter(([, isActive]) => isActive === true)
                        .map(([brandId]) => brandId);

                    if (activeBrands.length > 0) {
                        byPos[vpos] = activeBrands;
                    }
                });

                setActivePosBrands(byPos);
            } catch (e) {
                console.error("Active bank brands load error", e);
                setActivePosBrands({});
            }
        },
        []
    );

    // 1) Eski endpoint: aktif POS kayıtları + banka/taksit bilgisi
    useEffect(() => {
        if (!companyId) return;
        loadActivePos(companyId);
    }, [companyId, loadActivePos, refreshKey]);

    // 2) Aktif kart brandleri (ParamPOS + PayTR)
    useEffect(() => {
        if (!companyId) return;
        loadActiveBrands(companyId);
    }, [companyId, loadActiveBrands, refreshKey]);

    const loadOrphans = useCallback(async (cid) => {
        if (!cid) return;
        try {
            const res = await getActivePosOrphanSummary(cid);
            const data = res?.data;
            const groups = data?.orphanGroups || [];
            const targets = data?.transferTargets || [];
            setTransferTargets(targets);
            setOrphanCount(groups.reduce((n, g) => n + (g.cardBrands?.length || 0), 0));
            setOrphanTarget((prev) => (targets.includes(prev) ? prev : (targets[0] || "")));
        } catch {
            setTransferTargets([]);
            setOrphanCount(0);
        }
    }, []);

    useEffect(() => {
        if (!companyId) return;
        loadOrphans(companyId);
    }, [companyId, loadOrphans, refreshKey, banks]);

    const moveOrphansToTarget = async () => {
        if (!orphanTarget) return;
        // Kaynaklar mevcut satırlardan türetilir: hedef dışındaki her POS taşınır.
        // Böylece öksüz marka kalmasa da "hepsini aktar" iş görür.
        const sources = Array.from(
            new Set(banks.map((b) => b.virtualPos).filter((vp) => vp && vp !== orphanTarget))
        );
        if (sources.length === 0) {
            toast.info(`Tüm kart markaları zaten ${VPOS_LABELS[orphanTarget] || orphanTarget} üzerinde.`);
            return;
        }

        setMovingOrphans(true);
        try {
            let moved = 0;
            for (const source of sources) {
                const res = await migrateAllBrandsToVirtualPos(companyId, {
                    sourceVirtualPos: source,
                    targetVirtualPos: orphanTarget,
                });
                if (res?.statusCode !== 200) {
                    toast.error(res?.message || "Kart markaları aktarılamadı.");
                    return;
                }
                moved += Number(res?.data) || 0;
            }
            toast.success(`${moved} kart markası ${VPOS_LABELS[orphanTarget] || orphanTarget} üzerine taşındı.`);
            await loadActivePos(companyId);
        } catch {
            toast.error("Kart markaları aktarılamadı.");
        } finally {
            setMovingOrphans(false);
        }
    };

    const posOptions = useMemo(() => {
        const options = [];
        Object.entries(activePosBrands).forEach(([vpos, brands]) => {
            (brands || []).forEach((brandId) => {
                // Kart markası zaten bu POS'a atanmışsa seçenek listelenmez —
                // "Yapı Kredi - İşyerimPOS" seçiliyken listede tekrar çıkıp
                // "zaten aktif" uyarısına düşmesi kafa karıştırıyordu.
                const currentBank = banks.find((b) => b.cardBrand === brandId);
                if (currentBank?.virtualPos === vpos) {
                    return;
                }

                const bankCardLabel =
                    CARD_BRAND_BANK_LABELS[brandId] ||
                    CARD_BRAND_LABELS[brandId] ||
                    brandId;

                const vposLabel = VPOS_LABELS[vpos] || vpos;

                options.push({
                    id: `${brandId}_${vpos}`,
                    vpos,
                    brandId,
                    label: `${bankCardLabel} - ${vposLabel}`
                });
            });
        });

        return options;
    }, [activePosBrands, banks]);

    const filteredPosOptions = useMemo(() => {
        const q = posSearch.toLowerCase();
        return posOptions.filter((o) => o.label.toLowerCase().includes(q));
    }, [posSearch, posOptions]);

    // Bu kart markası şu an hangi sanal POS'ta aktif?
    const findCurrentVposForBrand = (brandId) => {
        for (const [vpos, brands] of Object.entries(activePosBrands)) {
            if ((brands || []).includes(brandId)) {
                return vpos;
            }
        }
        return null;
    };

    /**
     * Bağlı bir POS'a atanmış TÜM markaların taksit oranlarını tek seferde çeker.
     * Markalar sırayla işlenir: sağlayıcıya aynı anda 10 istek atmak oran
     * servislerinde zaman aşımına düşüyordu. Bir marka düşerse diğerleri devam
     * eder, sonuç toplu olarak bildirilir; liste en sonda bir kez tazelenir.
     */
    const handleRefreshAllInstallments = async () => {
        if (!companyId) return;
        const targets = displayCardBrands.filter((b) => b.connected);
        if (targets.length === 0) {
            toast.info("Bağlı bir POS'a atanmış kart markası yok.");
            return;
        }

        setRefreshingAll(true);
        let ok = 0;
        const failed = [];
        try {
            for (const brand of targets) {
                try {
                    const resp = await refreshBrandInstallments(companyId, brand.id);
                    if (resp?.statusCode === 200) ok++;
                    else failed.push(brand.label);
                } catch {
                    failed.push(brand.label);
                }
            }

            if (ok > 0) toast.success(`${ok} kart markasının taksit oranları güncellendi.`);
            if (failed.length > 0) {
                toast.error(`Oran çekilemeyen marka: ${failed.join(", ")}`);
            }

            await Promise.all([loadActivePos(companyId), loadActiveBrands(companyId)]);
        } finally {
            setRefreshingAll(false);
        }
    };

    const handleRefreshInstallments = async (brandId) => {
        if (!companyId) return;

        try {
            setRefreshingBrandId(brandId);

            const resp = await refreshBrandInstallments(companyId, brandId);

            if (resp?.statusCode !== 200) {
                toast.error(resp?.message || "Taksit oranları çekilirken bir hata oluştu");
                return;
            }

            toast.success(resp?.message || "Taksit oranları güncellendi");
            await Promise.all([
                loadActivePos(companyId),
                loadActiveBrands(companyId)
            ]);
        } catch (e) {
            console.error("Refresh installments error", e);
            toast.error(e?.message || "Taksit oranları çekilirken beklenmeyen bir hata oluştu");
        } finally {
            setRefreshingBrandId(null);
        }
    };

    const handleToggleStandart = async (enabled) => {
        if (!companyId) return;
        try {
            setStandartSaving(true);
            const resp = await setActivePosStandart(companyId, enabled);
            if (resp?.statusCode !== 200) {
                toast.error(resp?.message || "Banka kartı ayarı kaydedilemedi");
                return;
            }
            toast.success(resp?.message);
            await loadActivePos(companyId);
        } catch (e) {
            toast.error(e?.message || "Banka kartı ayarı kaydedilemedi");
        } finally {
            setStandartSaving(false);
        }
    };

    const handleSelectOption = (option) => {
        setPosSearch("");
        setPosDropdownOpen(false);

        const targetVpos = option.vpos;
        const brandId = option.brandId;

        // Bu kart markasının ŞU ANKİ POS'u, gerçek yönlendirmeden (active_pos_bank = banks) okunur.
        const currentBank = banks.find((b) => b.cardBrand === brandId);
        const currentVpos = currentBank?.virtualPos || null;

        // Zaten hedef POS'taysa yapacak bir şey yok.
        if (currentVpos === targetVpos) {
            toast.info("Bu kart markası zaten seçilen POS'ta aktif.");
            return;
        }

        const brandLabel =
            CARD_BRAND_BANK_LABELS[brandId] ||
            CARD_BRAND_LABELS[brandId] ||
            brandId;

        const fromVposLabel = currentVpos ? (VPOS_LABELS[currentVpos] || currentVpos) : null;
        const toVposLabel = VPOS_LABELS[targetVpos] || targetVpos;

        setPendingMigration({
            cardBrand: brandId,
            brandLabel,
            fromVirtualPos: currentVpos,
            toVirtualPos: targetVpos,
            fromLabel: fromVposLabel ? `${brandLabel} - ${fromVposLabel}` : brandLabel,
            toLabel: `${brandLabel} - ${toVposLabel}`
        });
    };

    const handleCancelMigration = () => {
        setPendingMigration(null);
        setIsMigrating(false);
    };

    const handleConfirmMigration = async () => {
        if (!pendingMigration || !companyId) return;

        try {
            setIsMigrating(true);

            const resp = await migrateBrandToVirtualPos(companyId, {
                cardBrand: pendingMigration.cardBrand,
                virtualPos: pendingMigration.toVirtualPos
            });

            if (resp?.statusCode !== 200) {
                toast.error(resp?.message || "Kart markası taşınırken bir hata oluştu");
                return;
            }
            toast.success(resp?.message || "Kart markası başarıyla taşındı");

            await Promise.all([
                loadActivePos(companyId),
                loadActiveBrands(companyId)
            ]);
        } catch (e) {
            console.error("Brand migration error", e);
            toast.error(e?.message || "Kart markası taşınırken beklenmeyen bir hata oluştu");
        } finally {
            setIsMigrating(false);
            setPendingMigration(null);
        }
    };

    const displayCardBrands = useMemo(() => {
        const brandIds = new Set();

        banks.forEach((b) => {
            if (b.cardBrand) brandIds.add(b.cardBrand);
        });

        Object.values(activePosBrands).forEach((brandArr) => {
            (brandArr || []).forEach((brandId) => brandIds.add(brandId));
        });

        const connectedBrandIds = new Set(
            banks.filter((b) => b.posConnected && b.cardBrand).map((b) => b.cardBrand)
        );
        const vposByBrand = new Map(
            banks.filter((b) => b.cardBrand).map((b) => [b.cardBrand, b.virtualPos])
        );

        return Array.from(brandIds)
            .map((brandId) => {
                const bankLabel = CARD_BRAND_BANK_LABELS[brandId];
                const brandLabel = CARD_BRAND_LABELS[brandId] || brandId;

                return {
                    id: brandId,
                    label: bankLabel || brandLabel,
                    connected: connectedBrandIds.has(brandId),
                    virtualPos: vposByBrand.get(brandId) || "",
                    closed: brandId === "STANDART" && !banks.some((b) => b.cardBrand === brandId
                        && (b.installments || []).some((i) => i.installmentCount === 1 && i.enabled)),
                };
            })
            // Sıra: önce bağlı POS'a atanmış markalar (ödeme alabilenler), sonra aynı
            // POS'takiler yan yana gelsin diye POS'a göre, en son ada göre — böylece
            // liste her yüklemede aynı düzende gelir. Kapalı banka kartı en sonda durur.
            .sort((a, b) => {
                if (a.closed !== b.closed) return a.closed ? 1 : -1;
                if (a.connected !== b.connected) return a.connected ? -1 : 1;
                if (a.virtualPos !== b.virtualPos) return a.virtualPos.localeCompare(b.virtualPos, "tr");
                return a.label.localeCompare(b.label, "tr");
            });
    }, [banks, activePosBrands]);

    const getBanksForBrand = (brandId) =>
        banks.filter((b) => b.cardBrand === brandId && Array.isArray(b.installments));

    // Kapalı taksit "oran: %0" DEĞİL, "-" gösterilir: sağlayıcı o taksit için oran
    // yayımlamadığında backend seçeneği enabled=false yazıyor; %0 basmak müşteriye
    // olmayan bir komisyonsuz taksit vaat etmek olurdu.
    const getRateForInstallment = (bank, installmentCount) => {
        const opt = bank?.installments?.find((i) => i.installmentCount === installmentCount);
        if (!opt || opt.enabled === false) return null;
        const num = Number(opt.commissionPercent);
        if (Number.isNaN(num)) return opt.commissionPercent; // string gelebilir
        return num;
    };

    // Açık kart markasının taksit detayları (grid altında tam genişlik panelde gösterilir)
    const openBrand = openCardBrand ? displayCardBrands.find((b) => b.id === openCardBrand) : null;
    const openBrandPrimaryBank = openBrand ? getBanksForBrand(openBrand.id)[0] : null;

    return (
        <>
            <hr className="my-4" />

            <h4 style={{ color: NAVY }}>Aktif POS&apos;lar</h4>
            <p className="text-muted mb-3">
                Buradan aktif POS sağlayıcılarınızı yönetebilir ve kart markalarına göre taksit yapılarını
                inceleyebilirsiniz.
            </p>

            {/* Aktif POS ekle: sadece /active-bank-brands’ten gelen aktif POS'lar */}
            <div className="mb-3 position-relative" style={{ maxWidth: 560, width: "100%" }}>
                <Input
                    value={posSearch}
                    onChange={(e) => {
                        setPosSearch(e.target.value);
                        if (!posDropdownOpen) setPosDropdownOpen(true);
                    }}
                    onFocus={() => setPosDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setPosDropdownOpen(false), 150)}
                    placeholder="Aktif POS ekle"
                />

                {posDropdownOpen && (
                    <div
                        className="border rounded bg-white shadow-sm position-absolute w-100 mt-1"
                        style={{ zIndex: 10, maxHeight: 240, overflowY: "auto" }}
                    >
                        {filteredPosOptions.length === 0 ? (
                            <div className="px-3 py-2 text-muted small">Sonuç bulunamadı</div>
                        ) : (
                            filteredPosOptions.map((option) => (
                                <div
                                    key={option.id}
                                    className="px-3 py-2"
                                    style={{ cursor: "pointer" }}
                                    onMouseDown={() => handleSelectOption(option)}
                                >
                                    <div className="fw-semibold">{option.label}</div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {loading && (
                <div className="d-flex align-items-center gap-2 text-muted mb-3">
                    <Spinner size="sm" /> <span>Aktif POS bilgileri yükleniyor...</span>
                </div>
            )}

            {/* Bağlı olmayan bir POS'ta kalan markaları tek seferde taşımak için.
                Uyarı kutusu değil, sade bir araç satırı: yalnız taşınacak marka
                ve gidebileceği bir POS varken görünür. */}
            {transferTargets.length > 0 && (
                <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                    <span className="text-muted" style={{ fontSize: 13 }}>
                        {orphanCount > 0
                            ? <>Bağlı olmayan POS&apos;taki <strong>{orphanCount} marka</strong></>
                            : <>Tüm kart markalarını taşı</>}
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 170 }}
                        value={orphanTarget}
                        onChange={(e) => setOrphanTarget(e.target.value)}
                        disabled={movingOrphans}
                    >
                        {transferTargets.map((t) => (
                            <option key={t} value={t}>{VPOS_LABELS[t] || t}</option>
                        ))}
                    </select>
                    <Button color="light" size="sm" className="border" onClick={moveOrphansToTarget} disabled={movingOrphans || refreshingAll}>
                        {movingOrphans
                            ? <><Spinner size="sm" className="me-1" />Aktarılıyor...</>
                            : <><i className="bx bx-transfer-alt me-1" />Hepsini aktar</>}
                    </Button>
                    <Button
                        color="light"
                        size="sm"
                        className="border"
                        onClick={handleRefreshAllInstallments}
                        disabled={movingOrphans || refreshingAll}
                        title="Bağlı POS'a atanmış tüm kart markaları için taksit oranlarını sağlayıcıdan yeniden çeker."
                    >
                        {refreshingAll
                            ? <><Spinner size="sm" className="me-1" />Oranlar çekiliyor...</>
                            : <><i className="bx bx-download me-1" />Taksit oranlarını çek</>}
                    </Button>
                </div>
            )}

            {/* Hiç bağlantı yokken grid boş bir alan olarak kalıyordu. Artık kart markası
                satırları yalnız gerçekten yapılandırılmış bir POS varken oluşuyor
                (bkz. ActivePosInitializerService), o yüzden boş durum açıklanıyor. */}
            {!loading && displayCardBrands.length === 0 && (
                <div className="text-center text-muted border rounded-3 p-4 mt-3">
                    <i className="bx bx-plug d-block mb-2" style={{ fontSize: "1.6rem" }} />
                    <div className="fw-semibold">Henüz sanal POS bağlantısı yok</div>
                    <div className="small">
                        Yukarıdan bir sanal POS bağladığınızda kart markaları burada listelenir.
                    </div>
                </div>
            )}

            {/* Kart markaları — entegrasyon kartlarıyla aynı görünümde kart grid'i */}
            <div
                className="mt-3"
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: "1rem",
                }}
            >
                {displayCardBrands.map((brand) => {
                    const isOpen = openCardBrand === brand.id;
                    const banksForBrand = getBanksForBrand(brand.id);

                    const vposForBrand = Array.from(
                        new Set(
                            banksForBrand
                                .map((b) => b.virtualPos)
                                .filter(Boolean)
                        )
                    );
                    const activeVpos = vposForBrand[0];
                    const vposLabel = activeVpos ? (VPOS_LABELS[activeVpos] || activeVpos) : null;
                    // Marka bir POS'a atanmış olabilir ama o POS'un ayarı silinmiş olabilir.
                    // Yeşil yalnız gerçekten bağlı POS'a ayrılır; bağlı değilse kart gri kalır
                    // (satır silinmiyor, kullanıcı markayı görüp başka POS'a taşıyabiliyor).
                    const posConnected = banksForBrand.some((b) => b.posConnected);
                    // Banka kartı elle açılana kadar kapalıdır; bağlı olsa da yeşil gösterilmez.
                    const standartClosed = brand.id === "STANDART" && !banksForBrand.some((b) =>
                        (b.installments || []).some((i) => i.installmentCount === 1 && i.enabled));
                    const active = posConnected && !standartClosed;

                    return (
                        <div
                            key={brand.id}
                            onClick={() => toggleCardBrand(brand.id)}
                            className="h-100 d-flex flex-column border-0 shadow-sm"
                            style={{
                                borderRadius: 16,
                                overflow: "hidden",
                                cursor: "pointer",
                                background: active
                                    ? "linear-gradient(180deg, rgba(47,158,68,.06), rgba(47,158,68,0))"
                                    : "#fff",
                                boxShadow: isOpen ? `0 0 0 2px ${NAVY}` : undefined,
                            }}
                        >
                            <div
                                style={{
                                    height: 4,
                                    background: active ? "linear-gradient(90deg, #2f9e44, #51cf66)" : "#e7eaf0",
                                }}
                            />

                            <div className="p-3 d-flex flex-column flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                    {/* Logo + isim yan yana — entegrasyon kartlarıyla aynı tip */}
                                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                        <div
                                            className="d-flex align-items-center justify-content-center bg-white shadow-sm"
                                            style={{ width: 64, height: 64, borderRadius: 16, overflow: "hidden", flexShrink: 0 }}
                                        >
                                            {CARD_BRAND_LOGOS[brand.id] ? (
                                                <img
                                                    src={CARD_BRAND_LOGOS[brand.id]}
                                                    alt={brand.label}
                                                    style={{ maxWidth: "82%", maxHeight: "82%", objectFit: "contain" }}
                                                />
                                            ) : (
                                                <i className="bx bx-credit-card" style={{ fontSize: 32, color: NAVY }} />
                                            )}
                                        </div>
                                        <h6
                                            className="fw-bold mb-0"
                                            style={{ color: "#1f2937", fontSize: 17, lineHeight: 1.25 }}
                                        >
                                            {brand.label}
                                        </h6>
                                    </div>
                                    <i
                                        className={`bx bx-chevron-${isOpen ? "up" : "down"} fs-5`}
                                        style={{ color: isOpen ? NAVY : "#9aa2b1", flexShrink: 0 }}
                                    />
                                </div>

                                <p className="text-muted mt-3 mb-3" style={{ fontSize: 13, lineHeight: 1.35 }}>
                                    Sanal POS taksit yapısı
                                </p>

                                <div className="d-flex align-items-center justify-content-between mt-auto pt-1 gap-2">
                                    <span
                                        className="d-inline-flex align-items-center fw-semibold"
                                        style={{
                                            color: active ? "#2f9e44" : "#5c6a86",
                                            backgroundColor: active ? "rgba(47,158,68,.12)" : "rgba(92,106,134,.12)",
                                            borderRadius: 999,
                                            padding: ".25rem .7rem",
                                            fontSize: 12,
                                        }}
                                        title={standartClosed
                                            ? "Banka kartıyla ödeme kapalı. Açmak için karta tıklayın."
                                            : activeVpos && !posConnected
                                                ? "Bu marka için bağlı bir sanal POS yok. Karta tıklayıp bağlı bir POS'a atayabilirsiniz."
                                                : undefined}
                                    >
                                        <i
                                            className={`bx ${active ? "bx-check-circle" : standartClosed ? "bx-block" : "bx-plug"} me-1`}
                                            style={{ fontSize: 13 }}
                                        />
                                        {/* Bağlı değilken POS adı YAZILMAZ: satırdaki ad, kolon NOT NULL
                                            olduğu için eski kodun yazdığı dolgu olabiliyor (hiç bağlanmamış
                                            PayTR gibi) ve kullanıcıya yanlış bilgi veriyordu. */}
                                        {!posConnected ? "Bağlı POS yok" : standartClosed ? `${vposLabel} · Kapalı` : vposLabel}
                                    </span>
                                    <span className="small fw-semibold" style={{ color: NAVY }}>
                                        {isOpen ? "Gizle" : "Taksitler"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Seçili markanın taksit oranları */}
            {openBrand && (
                <div className="mt-3 p-3 border-0 shadow-sm" style={{ borderRadius: 16, background: "#fff" }}>
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                        <h6 className="fw-bold mb-0" style={{ color: NAVY }}>
                            {openBrand.label} — Taksit Oranları
                        </h6>
                        {openBrand.id === "STANDART" && openBrandPrimaryBank && (
                            <div className="form-check form-switch mb-0 me-auto ms-3">
                                <Input
                                    type="switch"
                                    id="standart-switch"
                                    checked={getRateForInstallment(openBrandPrimaryBank, 1) != null}
                                    disabled={standartSaving}
                                    onChange={(e) => handleToggleStandart(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="standart-switch">
                                    Banka kartıyla tek çekim ödeme al
                                </label>
                            </div>
                        )}
                        <Button
                            size="sm"
                            style={{ backgroundColor: NAVY, borderColor: NAVY, color: "#fff", borderRadius: 999 }}
                            onClick={() => handleRefreshInstallments(openBrand.id)}
                            disabled={refreshingBrandId === openBrand.id}
                        >
                            {refreshingBrandId === openBrand.id ? "Çekiliyor..." : "Taksit oranlarını çek"}
                        </Button>
                    </div>

                    <Row className="g-2">
                        {Array.from({ length: 12 }, (_, index) => {
                            const installment = index + 1;
                            const rate = openBrandPrimaryBank
                                ? getRateForInstallment(openBrandPrimaryBank, installment)
                                : null;

                            const unavailable = rate == null;
                            const rateText =
                                unavailable
                                    ? "Oran: -"
                                    : typeof rate === "number"
                                        ? `Oran: %${rate.toFixed(2)}`
                                        : `Oran: %${rate}`;

                            return (
                                <Col key={installment} xs={4} md={2}>
                                    <div
                                        className="text-center py-2"
                                        style={{ background: "#f4f6fb", borderRadius: 12, opacity: unavailable ? 0.55 : 1 }}
                                        title={unavailable ? "Bu POS bu kart için taksit oranı yayımlamıyor; yalnız tek çekim yapılabilir." : undefined}
                                    >
                                        <div className="fw-bold">{installment === 1 ? "Tek Çekim" : `${installment} Taksit`}</div>
                                        <div className="small text-muted">{rateText}</div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}

            <Modal
                isOpen={!!pendingMigration}
                toggle={handleCancelMigration}
                centered
            >
                <ModalHeader toggle={handleCancelMigration}>
                    Farklı POS&apos;a taşı
                </ModalHeader>
                <ModalBody>
                    {pendingMigration && (
                        <p className="mb-0">
                            {/* "X yerine Y'yi aktif et" kalıbı kart markası iki kez geçtiği için
                                kafa karıştırıyordu; taşınan şeyin MARKA, değişenin POS olduğu
                                netleştirildi. */}
                            <strong>{pendingMigration.brandLabel}</strong> kartı{" "}
                            {pendingMigration.fromVirtualPos ? (
                                <>
                                    <strong>{VPOS_LABELS[pendingMigration.fromVirtualPos] || pendingMigration.fromVirtualPos}</strong>
                                    {"'dan alınıp "}
                                </>
                            ) : null}
                            <strong>{VPOS_LABELS[pendingMigration.toVirtualPos] || pendingMigration.toVirtualPos}</strong>
                            {"'a taşınacak ve taksit oranları bu POS'tan yeniden çekilecek. Onaylıyor musunuz?"}
                        </p>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCancelMigration} disabled={isMigrating}>
                        Vazgeç
                    </Button>
                    <Button
                        style={{ backgroundColor: NAVY, borderColor: NAVY }}
                        onClick={handleConfirmMigration}
                        disabled={isMigrating}
                    >
                        {isMigrating ? "Güncelleniyor..." : "Evet, taşı"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}